import type { Country } from "@/i18n/config"

/**
 * The base blood check-up a market sells, as a `{ slug, labelKey }` pair.
 *
 * Germany replaced the Advanced blood test with the entry-level
 * `prevention-starter` ("Vorsorge-Starter", €199, 67 markers): the DE market
 * hides `advanced-blood-test` (`{ "de-de": false }`, 308-redirected) and sells
 * `prevention-starter` instead. Every other market keeps the Advanced blood
 * test, but the Netherlands sells a reduced panel under that same name and URL
 * (AHD-13560), tagged `advanced-blood-test-nl` in the CMS. This is the single source of truth for "which blood product does this
 * market link to and label", so no surface hardcodes `country === "de"` to pick
 * between the two — nav, footer, homepage cards, the DE city LPs, the quiz,
 * search presets and the markdown mirrors all resolve through here.
 *
 * `labelKey` is a `nav`-namespace translation key (both labels live there):
 * `preventionStarter` → "Vorsorge-Starter" on DE, `advancedBloodTest` →
 * "Advanced blood test" elsewhere.
 */
export interface BaseBloodTest {
  /** `/tests/<slug>` product page for this market's base blood check-up. */
  slug: string
  /** `nav`-namespace label key for the product's name. */
  labelKey: "preventionStarter" | "advancedBloodTest"
  /**
   * CMS `packages` tag of the biomarkers this product measures, for
   * `BiomarkerDiseaseExplorer`. On DE this is the same tag the copy's
   * `%panelCount%` is counted from, so the list and the number cannot drift.
   */
  biomarkerPackage: "prevention-starter" | "blood" | "advanced-blood-test-nl"
}

const PREVENTION_STARTER: BaseBloodTest = {
  slug: "prevention-starter",
  labelKey: "preventionStarter",
  biomarkerPackage: "prevention-starter",
}

const ADVANCED_BLOOD_TEST: BaseBloodTest = {
  slug: "advanced-blood-test",
  labelKey: "advancedBloodTest",
  biomarkerPackage: "blood",
}

/**
 * The Dutch Advanced blood test: same page, slug and name as everywhere else,
 * but its panel is the Prevention Starter set minus the markers NL hides
 * (`NL_PANEL_PACKAGE` in lib/de-panel-biomarkers.ts, which the copy's
 * `%panelCount%` is counted from).
 */
const ADVANCED_BLOOD_TEST_NL: BaseBloodTest = {
  ...ADVANCED_BLOOD_TEST,
  biomarkerPackage: "advanced-blood-test-nl",
}

/** The base blood check-up product for `country`. */
export function baseBloodTest(country: Country | string): BaseBloodTest {
  if (country === "de") return PREVENTION_STARTER
  if (country === "nl") return ADVANCED_BLOOD_TEST_NL
  return ADVANCED_BLOOD_TEST
}

/**
 * A test page's biomarker package in this market. The Advanced blood test's
 * `blood` tag stands for the market's own panel where that differs (NL);
 * every other package, and every other market, is returned unchanged.
 */
export function marketBiomarkerPackage(
  pkg: string,
  country: Country | string
): string {
  const base = baseBloodTest(country)
  return pkg === "blood" && base.slug === ADVANCED_BLOOD_TEST.slug
    ? base.biomarkerPackage
    : pkg
}

/**
 * Whether a /go LP shows a sample reading in this market. The LPs illustrate
 * their stories with markers from the Advanced blood test; the reduced NL
 * panel (AHD-13560) does not measure some of them (HOMA-IR, fT4, ESR), so
 * those rows are hidden there rather than shown as results NL never reports.
 *
 * A per-row predicate, not a filter: the LP keeps mapping over its literal
 * readings, so `scripts/route-namespaces.ts` can still evaluate
 * `t(r.markerKey)` and ship the `go` keys one by one instead of the whole
 * namespace.
 */
export function showsReading(
  markerKey: string,
  country: Country | string,
  notInReducedPanel: ReadonlySet<string>
): boolean {
  return (
    baseBloodTest(country).biomarkerPackage !== "advanced-blood-test-nl" ||
    !notInReducedPanel.has(markerKey)
  )
}
